import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}

export function usePushNotifications(token) {
    const [isSupported, setIsSupported] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [permission, setPermission] = useState('default');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [registration, setRegistration] = useState(null);

    // Check if push notifications are supported
    useEffect(() => {
        const checkSupport = () => {
            const supported = 'serviceWorker' in navigator && 
                             'PushManager' in window && 
                             'Notification' in window;
            setIsSupported(supported);
            
            if ('Notification' in window) {
                setPermission(Notification.permission);
            }
        };
        checkSupport();
    }, []);

    // Register service worker and check subscription
    useEffect(() => {
        if (!isSupported) return;

        const registerSW = async () => {
            try {
                const reg = await navigator.serviceWorker.register('/push-service-worker.js');
                setRegistration(reg);
                
                // Check existing subscription
                const subscription = await reg.pushManager.getSubscription();
                setIsSubscribed(!!subscription);
            } catch (err) {
                console.error('Service worker registration failed:', err);
                setError('Failed to register service worker');
            }
        };

        registerSW();
    }, [isSupported]);

    // Subscribe to push notifications
    const subscribe = useCallback(async () => {
        if (!isSupported || !registration || !token) {
            setError('Push notifications not available');
            return false;
        }

        setLoading(true);
        setError(null);

        try {
            // Request notification permission
            const permissionResult = await Notification.requestPermission();
            setPermission(permissionResult);
            
            if (permissionResult !== 'granted') {
                setError('Notification permission denied');
                return false;
            }

            // Get VAPID public key from backend
            const configResponse = await fetch(`${API}/api/push/config`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!configResponse.ok) {
                throw new Error('Failed to get push configuration');
            }
            
            const { vapidPublicKey } = await configResponse.json();

            // Subscribe to push manager
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
            });

            // Send subscription to backend
            const response = await fetch(`${API}/api/push/subscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    subscription: subscription.toJSON()
                })
            });

            if (!response.ok) {
                throw new Error('Failed to save subscription');
            }

            setIsSubscribed(true);
            return true;
        } catch (err) {
            console.error('Push subscription error:', err);
            setError(err.message || 'Failed to subscribe');
            return false;
        } finally {
            setLoading(false);
        }
    }, [isSupported, registration, token]);

    // Unsubscribe from push notifications
    const unsubscribe = useCallback(async () => {
        if (!registration || !token) return false;

        setLoading(true);
        setError(null);

        try {
            const subscription = await registration.pushManager.getSubscription();
            
            if (subscription) {
                // Unsubscribe from push manager
                await subscription.unsubscribe();
                
                // Notify backend
                await fetch(`${API}/api/push/unsubscribe`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        endpoint: subscription.endpoint
                    })
                });
            }

            setIsSubscribed(false);
            return true;
        } catch (err) {
            console.error('Push unsubscription error:', err);
            setError(err.message || 'Failed to unsubscribe');
            return false;
        } finally {
            setLoading(false);
        }
    }, [registration, token]);

    return {
        isSupported,
        isSubscribed,
        permission,
        loading,
        error,
        subscribe,
        unsubscribe
    };
}

export default usePushNotifications;
